import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import appIcon from '../../assets/iconW.svg'; 
import './Registration.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Registration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
  });
  const [errorMsg, setErrorMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNextStep = async () => { 
    setErrorMsg('');

    if (step === 1) {
      if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || formData.password.length < 6) {
        setErrorMsg('Please complete all fields. Password must be at least 6 characters.');
        return;
      }
      setStep(2);
      return;
    }

    if (step === 2) {
      setIsSubmitting(true);

      try {
        const response = await fetch(`${API_BASE}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            full_name: formData.fullName.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            password: formData.password,
            role: 'admin',
          }),
        });
        const result = await response.json();

        if (!response.ok) {
          setErrorMsg(result.detail || 'Registration failed');
          return;
        }

        setStep(3);
      } catch (error) {
        setErrorMsg('Unable to connect to the registration server');
      } finally {
        setIsSubmitting(false);
      }

    } else if (step === 3) {
      navigate('/login');
    }
  };

  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };


  return (
    <div className="signup-page-wrapper">
      <div className="signup-card">

        {/* Left Side: Branding Panel */}
        <div className="signup-left">
          <div className="brand-content">
            <img src={appIcon} alt="MURJAN Logo" className="signup-logo" />
            <h1 className="brand-title">MURJAN</h1>
            <p className="brand-subtitle">
              Coral Reef Operations Intelligence<br />Platform
            </p>
          </div>
        </div>

        {/* Right Side: Form Panel */}
        <div className="signup-right">

          {/* Top Stepper */}
          <div className="stepper-container">
            <div className="stepper-line">
              <div className="stepper-progress" style={{ width: `${(step / 3) * 100}%` }}></div>
            </div>
            <div className="steps" style={{ justifyContent: 'space-between' }}>
              <div className={`step ${step >= 1 ? 'active' : ''}`}>1</div>
              <div className={`step ${step >= 2 ? 'active' : ''}`}>2</div>
              <div className={`step ${step >= 3 ? 'active' : ''}`}>3</div>
            </div>
            <p className="step-text">Step {step} of 3</p>
          </div>

          {/* Dynamic Headers based on step */}
          <div className="form-header">
            {step === 1 && <h2>Personal Information</h2>}
            {step === 1 && <p>Create your MURJAN account</p>}
            
            {step === 2 && <h2>Review Information</h2>}
            {step === 2 && <p>Verify your details before submitting</p>}

            {step === 3 && <h2>Registration Complete</h2>}
            {step === 3 && <p>Your admin account is ready.</p>}
          </div>

          <form className="signup-form">
            {errorMsg && (
              <p style={{ color: '#EA6C52', margin: '0 0 15px 0', fontSize: '14px' }}>
                {errorMsg}
              </p>
            )}
            
            {/* --- STEP 1: Personal Info --- */}
            {step === 1 && (
              <>
                <div className="input-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input 
                    type="text" name="fullName" placeholder="Enter your full name" 
                    value={formData.fullName} onChange={handleChange} 
                  />
                </div>
                <div className="input-group">
                  <label>Email <span className="required">*</span></label>
                  <input 
                    type="email" name="email" placeholder="your.email@example.com" 
                    value={formData.email} onChange={handleChange} 
                  />
                </div>
                <div className="input-group">
                  <label>Phone Number <span className="required">*</span></label>
                  <input 
                    type="tel" name="phone" placeholder="+966 50 123 4567" 
                    value={formData.phone} onChange={handleChange} 
                  />
                </div>
                <div className="input-group">
                  <label>Password <span className="required">*</span></label>
                  <input 
                    type="password" name="password" placeholder="Create a password" 
                    value={formData.password} onChange={handleChange} 
                  />
                </div>
              </>
            )}

            {/* --- STEP 2: Review --- */}
            {step === 2 && (
              <div className="review-box">
                <div className="review-item">
                  <p className="review-label">Full Name</p>
                  <p className="review-value">{formData.fullName || 'Not provided'}</p>
                </div>
                <div className="review-item">
                  <p className="review-label">Email</p>
                  <p className="review-value">{formData.email || 'Not provided'}</p>
                </div>
                <div className="review-item">
                  <p className="review-label">Phone</p>
                  <p className="review-value">{formData.phone || 'Not provided'}</p>
                </div>
                <div className="review-item">
                  <p className="review-label">Role</p>
                  <p className="review-value">Admin</p>
                </div>
              </div>
            )}

            {/* --- STEP 3: Success / Go to Login --- */}
            {step === 3 && (
              <div className="success-box">
                <p>Registration successful! You can now log into the MURJAN Command Center using your credentials.</p>
              </div>
            )}

            <div className="button-group">
              {/* Only show Back button on Step 2 */}
              {step > 1 && step < 3 && (
                <button type="button" className="back-btn" onClick={handlePrevStep}>
                  Back
                </button>
              )}

            {/* Dynamic Button Text */}
            <button type="button" className="continue-btn" onClick={handleNextStep} disabled={isSubmitting}>
              {step === 1 && 'Continue'}
              {step === 2 && (isSubmitting ? 'Submitting...' : 'Submit Registration')}
              {step === 3 && 'Return to Login'}
            </button>
            </div>
          </form>

          {/* Hide the login link if they are on the success step */}
          {step < 3 && (
            <p className="login-link-text">
              Already have an account?{' '}
              <span onClick={() => navigate('/login')}>Log in</span>
            </p>
          )}

        </div>
      </div>
    </div>
  );
};

export default Registration;
